import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../../services/logger/logger';
import { useUnsavedChanges } from '../../../context/UnsavedChangesContext';
import UnsavedChangesModal from '../../../components/UnsavedChangesModal';
import DeliveryInformationCard from '../../../components/delivery/DeliveryInformationCard';
import ProductsTable from '../../../components/delivery/ProductsTable';
import { DeliveryProduct } from '../../../store/apiSlices/delivery/deliveryApiParser';
import {
  useCreateDeliveryMutation,
  useGetDeliveryProductsQuery,
  useGetProductsByVesselAndSupplierQuery,
  useGetProductsByVesselQuery,
  useUpdateDeliveryMutation,
} from '../../../store/apiSlices/delivery/deliveryApis';
import { useDeleteVesselProductMutation } from '../../../store/apiSlices/lubricationChart/lubricationChartsApis';
import ProductAddFlowContainer from './ProductAddFlowContainer';
import { showSuccessToast } from '../../../components/common/BaseToaster';
import { helpingMethod } from '../../../utils/HelpingMethod';

interface FormValues {
  vessel: string;
  supplier: number | string | null;
  port: string;
  pricelist: string;
  date: Date | string | any | null; // Date, string, or dayjs object, can be null
  poNumber: string;
  invoiced: boolean | undefined;
  invoiceNumber: string | undefined;
  remarks: string;
  salesTax: string;
  localTax: string;
}

interface ProductListingContainerProps {
  deliveryFormData: FormValues;
  selectedType: string;
  onBack: () => void;
  vesselId?: number;
  isEditMode?: boolean;
  editData?: any; // Delivery data from edit mode (contains id, etc.)
  availableDates?: { startDate: string; endDate: string; id: number };
  managerOptions?: any[];
  vesselOptions?: any[];
  supplierOptions?: any[];
  portOptions?: any[];
  extraCostsOptions?: any[];
  refetchAvailableDates?: any;
  supplierIdForMakerList?: number | null;
  setIsDataAvailable: (_value: boolean) => void;
}

const DELIVERY_TYPE_MAP: Record<string, string> = {
  standard: 'STANDARD',
  adjusting: 'ADJUSTMENT',
  secondHand: 'ACQUISITION',
  newBuilding: 'NEW_BUILDING',
};

const PRODUCT_FIELDS: Record<string, string[]> = {
  ADJUSTMENT: ['storageQuantity', 'sumpTankQuantity'],
  STANDARD: ['quantity', 'netValue', 'packagingCost'],
  ACQUISITION: ['netValue', 'storageQuantity', 'sumpTankQuantity'],
  NEW_BUILDING: ['quantity', 'netValue', 'packagingCost'],
};

const ProductListingContainer: React.FC<ProductListingContainerProps> = ({
  deliveryFormData,
  selectedType,
  onBack,
  vesselId,
  isEditMode = false,
  editData,
  availableDates,
  managerOptions,
  vesselOptions,
  supplierOptions,
  portOptions,
  extraCostsOptions,
  refetchAvailableDates,
  supplierIdForMakerList,
  setIsDataAvailable,
}) => {
  const { t } = useTranslation();
  // State management
  const [localProducts, setLocalProducts] = useState<DeliveryProduct[]>([]);
  const { registerUnsavedChanges, clearUnsavedChanges } = useUnsavedChanges();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showLubricationChart, setShowLubricationChart] = useState<boolean>(false);
  const [productAddFlowKey, setProductAddFlowKey] = useState(0);

  // Track if products have been initialized to prevent overwriting user input
  const productsInitializedRef = useRef(false);

  // Get vesselId from props or form data (like ROBS does)
  // These must be declared before storageKey since they're used in its calculation
  const effectiveVesselId =
    vesselId || (deliveryFormData.vessel ? parseInt(deliveryFormData.vessel) : 0);
  const deliveryId = editData?.id || editData?.srno;
  const supplierId = deliveryFormData.supplier ? parseInt(String(deliveryFormData.supplier)) : 0;

  // Generate a unique key for sessionStorage based on delivery context
  const storageKey = `delivery_edits_${isEditMode ? 'edit' : 'create'}_${deliveryId || 'new'}_${effectiveVesselId}_${selectedType}`;

  // Track user edits specifically to preserve them across API refetches
  // Initialize with empty object, will load from sessionStorage in useEffect
  const userEditsRef = useRef<Record<number, Partial<DeliveryProduct>>>({});

  // Determine which API to use based on delivery type
  const useSupplierAPI =
    (selectedType === 'standard' || selectedType === 'newBuilding') && supplierId > 0;

  // API hook for delivery products (for edit mode - prefilled products with values)
  const {
    data: deliveryProducts = [],
    isLoading: isLoadingDeliveryProducts,
    refetch: refetchDeliveryProducts,
  } = useGetDeliveryProductsQuery(
    {
      deliveryId: deliveryId as number,
      vesselId: effectiveVesselId,
      type: selectedType,
    },
    {
      skip:
        !isEditMode ||
        !deliveryId ||
        !effectiveVesselId ||
        !selectedType ||
        effectiveVesselId === 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  // API hook for products by vessel only (for adjusting and 2nd hand vessel)
  const {
    data: productsWithoutSupplier = [],
    isLoading: isLoadingProductsWithoutSupplier,
    refetch: refetchProductsWithoutSupplier,
  } = useGetProductsByVesselQuery(
    { vesselId: effectiveVesselId },
    {
      skip: isEditMode || !effectiveVesselId || effectiveVesselId === 0 || useSupplierAPI,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  // API hook for products by vessel and supplier (for standard and new building)
  const {
    data: productsWithSupplier = [],
    isLoading: isLoadingProductsWithSupplier,
    refetch: refetchProductsWithSupplier,
  } = useGetProductsByVesselAndSupplierQuery(
    { vesselId: effectiveVesselId, supplierId: supplierId },
    {
      skip: isEditMode || !effectiveVesselId || effectiveVesselId === 0 || !useSupplierAPI,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  // Combine results based on mode and which API was used
  const products = isEditMode
    ? deliveryProducts
    : useSupplierAPI
      ? productsWithSupplier
      : productsWithoutSupplier;
  const isLoadingProducts = isEditMode
    ? isLoadingDeliveryProducts
    : useSupplierAPI
      ? isLoadingProductsWithSupplier
      : isLoadingProductsWithoutSupplier;
  const refetchProducts = isEditMode
    ? refetchDeliveryProducts
    : useSupplierAPI
      ? refetchProductsWithSupplier
      : refetchProductsWithoutSupplier;

  const [createDelivery, { isLoading: isCreating }] = useCreateDeliveryMutation();
  const [updateDelivery, { isLoading: isUpdating }] = useUpdateDeliveryMutation();
  const [deleteVesselProduct] = useDeleteVesselProductMutation();

  // Track products added in this session to undo on discard
  const addedProductsRef = useRef<number[]>([]);
  const prevProductsRef = useRef<DeliveryProduct[]>([]);

  // Force refetch API when component mounts or vesselId changes
  useEffect(() => {
    if (isEditMode) {
      // In edit mode, refetch when deliveryId, vesselId, and type are available
      if (deliveryId && effectiveVesselId && effectiveVesselId !== 0 && selectedType) {
        refetchProducts();
      }
    } else {
      // In create mode, refetch when vesselId is available
      if (effectiveVesselId && effectiveVesselId !== 0) {
        refetchProducts();
      }
    }
  }, [effectiveVesselId, deliveryId, selectedType, isEditMode, refetchProducts]);

  // Load user edits from sessionStorage on mount or when storageKey changes
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        userEditsRef.current = JSON.parse(stored);
      }
    } catch (error) {
      logger.error('Failed to load edits from sessionStorage:', error as Error);
      userEditsRef.current = {};
    }
  }, [storageKey]);

  // Reset initialization flag when key dependencies change (component remounts or dependencies change)
  // Use refs to track previous values to detect actual changes
  const prevDepsRef = useRef({
    isEditMode,
    deliveryId,
    effectiveVesselId,
    selectedType,
  });

  useEffect(() => {
    const prev = prevDepsRef.current;
    const hasActualChange =
      prev.isEditMode !== isEditMode ||
      prev.deliveryId !== deliveryId ||
      prev.effectiveVesselId !== effectiveVesselId ||
      prev.selectedType !== selectedType;

    // Only reset if there's an actual change in the delivery context
    // Don't reset when just navigating between steps (back to form and proceed again)
    if (hasActualChange) {
      productsInitializedRef.current = false;
      userEditsRef.current = {}; // Clear user edits on full reset
      setLocalProducts([]); // Clear products when dependencies change

      // Clear sessionStorage for the old context
      try {
        // Generate the old storage key from previous values
        const oldKey = `delivery_edits_${prev.isEditMode ? 'edit' : 'create'}_${prev.deliveryId || 'new'}_${prev.effectiveVesselId}_${prev.selectedType}`;
        sessionStorage.removeItem(oldKey);
      } catch (error) {
        logger.error('Failed to clear old sessionStorage:', error as Error);
      }

      // Update the ref with current values
      prevDepsRef.current = {
        isEditMode,
        deliveryId,
        effectiveVesselId,
        selectedType,
      };
    }
  }, [isEditMode, deliveryId, effectiveVesselId, selectedType]);

  // Set products based on mode - initialize when data arrives
  // Don't overwrite if user has already made changes (checked via productsInitializedRef)
  // Track added products
  useEffect(() => {
    if (prevProductsRef.current.length > 0 && products.length > prevProductsRef.current.length) {
      const newProducts = products.filter(
        (p) => !prevProductsRef.current.find((pp) => pp.id === p.id),
      );
      newProducts.forEach((p) => {
        if (!addedProductsRef.current.includes(p.id)) {
          addedProductsRef.current.push(p.id);
        }
      });
    }
    prevProductsRef.current = products;
  }, [products]);

  useEffect(() => {
    if (products.length > 0) {
      setLocalProducts((prevLocalProducts) => {
        // Merge API products with local products/user edits
        const mergedProducts = products.map((apiProduct) => {
          // Priority 1: User explicit edits (stored in Ref)
          const userEdit = userEditsRef.current[apiProduct.id];

          // Priority 2: Existing local state (if we are merging)
          const localProduct = prevLocalProducts.find((p) => p.id === apiProduct.id);

          if (userEdit) {
            return {
              ...apiProduct,
              ...userEdit,
            };
          }

          if (localProduct) {
            // Fallback to localProduct for safety (e.g. initial load in Edit Mode)
            return {
              ...apiProduct,
              quantity: localProduct.quantity,
              netValue: localProduct.netValue,
              packagingCost: localProduct.packagingCost,
              storageQuantity: localProduct.storageQuantity,
              sumpTankQuantity: localProduct.sumpTankQuantity,
            };
          }
          return apiProduct;
        });

        productsInitializedRef.current = true;
        return mergedProducts;
      });
    }
  }, [products]);

  // Update isDataAvailable when any product has a value in editable fields
  useEffect(() => {
    const hasData = localProducts.some(
      (p) => p.quantity || p.netValue || p.packagingCost || p.storageQuantity || p.sumpTankQuantity,
    );
    setIsDataAvailable(hasData);
  }, [localProducts, setIsDataAvailable]);

  // Create delivery payload function - handles all delivery types
  const createDeliveryPayload = useCallback(
    (formData: any, products: DeliveryProduct[], managementPeriodIdOverride?: number | null) => {
      const deliveryType = DELIVERY_TYPE_MAP[selectedType] || 'STANDARD';

      // 1. Resolve effective IDs
      const parsed = helpingMethod.safeParseInt(formData.vessel);

      const vesselIdValue =
        parsed !== null
          ? parsed
          : effectiveVesselId && effectiveVesselId > 0
            ? effectiveVesselId
            : null;

      const managerIdValue = helpingMethod.safeParseInt(formData.manager);
      const managementPeriodIdValue =
        typeof managementPeriodIdOverride === 'number'
          ? managementPeriodIdOverride
          : (availableDates?.id ?? 0) > 0
            ? availableDates?.id
            : null;

      // 2. Map line items based on type-specific fields
      const lineItems = products
        ?.map((itm) => {
          const product: any = { productId: itm?.id };
          const fields = PRODUCT_FIELDS[deliveryType] || PRODUCT_FIELDS.STANDARD;

          let hasData = false;
          fields.forEach((field) => {
            const val = helpingMethod.toNumber(itm[field as keyof DeliveryProduct]);
            if (val !== undefined) {
              product[field] = val;
              hasData = true;
            }
          });

          return hasData ? product : null;
        })
        .filter(Boolean);

      // 3. Build core payload
      const payload: any = {
        type: deliveryType,
        date: helpingMethod.formatDate(formData.date),
        currencyId: 19,
        lineItems,
        ...(vesselIdValue !== null && { vesselId: vesselIdValue }),
        ...(managerIdValue !== null && { managerId: managerIdValue }),
        ...(managementPeriodIdValue !== null && { managementPeriodId: managementPeriodIdValue }),
      };

      // 4. Handle Type-Specific Additional Fields
      if (deliveryType === 'ADJUSTMENT') {
        if (formData.remarks) payload.remarks = formData.remarks;
      } else if (deliveryType === 'STANDARD' || deliveryType === 'NEW_BUILDING') {
        const portId = helpingMethod.safeParseIntForPayload(formData.port);
        const supplierId = helpingMethod.safeParseIntForPayload(formData.supplier);

        Object.assign(payload, {
          ...(portId !== undefined && { portId }),
          ...(supplierId !== undefined && { supplierId }),
          ...(formData.poNumber && { poNumber: formData.poNumber }),
          ...(formData.invoiced !== undefined && { invoiced: formData.invoiced }),
          ...(formData.invoiceNumber && { invoiceNumber: formData.invoiceNumber }),
          ...(formData.remarks && { remarks: formData.remarks }),
          extraCosts: [],
        });

        // 5. Add Extra Costs if present
        if (Array.isArray(formData.extraCosts)) {
          formData.extraCosts.forEach((extraCostId: string) => {
            const fieldName = extraCostId.split('_').join('');
            const value = parseFloat(formData[fieldName]);
            if (value > 0) {
              payload.extraCosts.push({
                extraCostId: parseInt(extraCostId),
                value,
              });
            }
          });
        }
      }

      return payload;
    },
    [selectedType, effectiveVesselId, availableDates],
  );

  // Effects - products are set in the earlier useEffect that checks initialization

  // Generate delivery info from form data
  const deliveryInfo = useMemo(
    () => ({
      manager: (deliveryFormData as any).manager ? String((deliveryFormData as any).manager) : '-',
      navilands: deliveryFormData.vessel ? String(deliveryFormData.vessel) : '-',
      deliveryType: selectedType || '-',
      standardDelivery: (() => {
        if (!deliveryFormData.date) return '-';
        // Use helpingMethod.formatDate which correctly parses DD/MM/YYYY strings
        const formatted = helpingMethod.formatDate(deliveryFormData.date);
        return formatted || t('common.invalidDate');
      })(),
      shellMarine:
        selectedType === 'adjusting'
          ? deliveryFormData.remarks || ''
          : deliveryFormData.supplier
            ? String(deliveryFormData.supplier)
            : null,
      port: deliveryFormData.port ? String(deliveryFormData.port) : '-',
      usdProvision:
        deliveryFormData.salesTax || deliveryFormData.localTax
          ? `${t('form.salesTax')} (${
              deliveryFormData.salesTax || '0,00'
            } usd), ${t('form.localTax')} (${deliveryFormData.localTax || '0,00'} usd)`
          : t('common.dash'),
      extraCosts: (deliveryFormData as any).extraCosts || [],
      extraCostsValues: deliveryFormData,
      poNo: deliveryFormData.poNumber ? `${deliveryFormData.poNumber}` : '-',
      invoiceNo: deliveryFormData.invoiceNumber ? `${deliveryFormData.invoiceNumber}` : '-',
      managementPeriod: t('form.managerName'), // Default value since we removed meta API
    }),
    [deliveryFormData, selectedType, t],
  );

  // Event handlers
  const handleProductChange = useCallback(
    (productId: number, field: keyof DeliveryProduct, value: string): void => {
      // Store user edit in ref
      if (!userEditsRef.current[productId]) {
        userEditsRef.current[productId] = {};
      }
      userEditsRef.current[productId][field] = Number(value) as any; // Store as number for consistency

      // Persist to sessionStorage
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(userEditsRef.current));
      } catch (error) {
        logger.error('Failed to save edits to sessionStorage:', error as Error);
      }

      setLocalProducts((prev) =>
        prev.map((product) =>
          product.id === productId ? { ...product, [field]: Number(value) as any } : product,
        ),
      );
    },
    [storageKey],
  );

  const handleSaveDelivery = useCallback(
    async (updatedProducts?: DeliveryProduct[]): Promise<void> => {
      try {
        // Create API payload in container
        const productsForPayload =
          Array.isArray(updatedProducts) && updatedProducts.length > 0
            ? updatedProducts
            : localProducts;
        let mpId: number | null =
          availableDates && typeof availableDates.id === 'number' && availableDates.id > 0
            ? availableDates.id
            : null;

        if (!mpId && typeof refetchAvailableDates === 'function') {
          const res = await refetchAvailableDates();
          const resData: any = res?.data;
          if (resData && typeof resData.id === 'number' && resData.id > 0) {
            mpId = resData.id;
          }
        }

        const payload = createDeliveryPayload(deliveryFormData, productsForPayload, mpId);

        if (isEditMode && deliveryId && effectiveVesselId) {
          // Update existing delivery (edit mode)
          const res = await updateDelivery({
            deliveryId: deliveryId as number,
            vesselId: effectiveVesselId,
            payload,
          }).unwrap();
          showSuccessToast(res?.message);

          // Refetch products to get updated data from backend
          await refetchProducts();

          // Clear sessionStorage after successful save
          try {
            sessionStorage.removeItem(storageKey);
          } catch (error) {
            logger.error('Failed to clear sessionStorage:', error as Error);
          }

          // Navigate to deliveries page after successful update
          clearUnsavedChanges();
        } else {
          // Create new delivery
          const res = await createDelivery(payload).unwrap();
          showSuccessToast(res?.message);

          // Refetch products for newly created delivery
          if (effectiveVesselId) {
            await refetchProducts();
          }

          // Clear sessionStorage after successful save
          try {
            sessionStorage.removeItem(storageKey);
          } catch (error) {
            logger.error('Failed to clear sessionStorage:', error as Error);
          }

          // Navigate to deliveries page after successful create
          clearUnsavedChanges();
        }
      } catch (error: any) {
        logger.error('Failed to save delivery', error as Error);
      }
    },
    [
      createDelivery,
      updateDelivery,
      isEditMode,
      deliveryId,
      effectiveVesselId,
      localProducts,
      deliveryFormData,
      refetchProducts,
      availableDates,
      createDeliveryPayload,
      refetchAvailableDates,
      storageKey,
      clearUnsavedChanges,
    ],
  );

  // --- Unsaved Changes Logic ---
  const handleSaveDeliveryRef = useRef(handleSaveDelivery);
  useEffect(() => {
    handleSaveDeliveryRef.current = handleSaveDelivery;
  });

  const hasUnsavedChanges = Object.keys(userEditsRef.current).length > 0;

  const stableOnSave = useCallback(async () => {
    if (handleSaveDeliveryRef.current) {
      await handleSaveDeliveryRef.current();
    }
  }, []);

  const handleDiscardChanges = useCallback(async () => {
    try {
      // Undo added products
      if (addedProductsRef.current.length > 0 && effectiveVesselId) {
        await Promise.all(
          addedProductsRef.current.map((id) =>
            deleteVesselProduct({
              vesselId: effectiveVesselId,
              productId: id,
            }).unwrap(),
          ),
        );
        addedProductsRef.current = [];
      }

      // Clear user edits
      userEditsRef.current = {};
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      logger.error('Failed to discard changes:', error as Error);
    }
  }, [storageKey, effectiveVesselId, deleteVesselProduct]);

  useEffect(() => {
    registerUnsavedChanges(hasUnsavedChanges, stableOnSave, handleDiscardChanges);
  }, [hasUnsavedChanges, registerUnsavedChanges, stableOnSave, handleDiscardChanges]);

  // In CREATE mode, mark as having unsaved changes when component mounts
  // This ensures warning shows even if user hasn't entered any product values yet
  // because reaching this screen means they've already filled the form
  useEffect(() => {
    if (!isEditMode) {
      // Mark as having unsaved changes by adding a dummy entry to userEditsRef
      // Using -1 as a special marker that won't conflict with real product IDs
      (userEditsRef.current as any)[-1] = { _formData: true };
    }
  }, [isEditMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      registerUnsavedChanges(false);
    };
  }, [registerUnsavedChanges]);
  // -----------------------------

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowCancelModal(true);
    }
  }, [hasUnsavedChanges]);

  const handleCloseCancelModal = useCallback(() => {
    setShowCancelModal(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setShowCancelModal(false);
    handleDiscardChanges();
    clearUnsavedChanges();
  }, [handleDiscardChanges, clearUnsavedChanges]);

  const handleOpenLubricationChart = useCallback((): void => {
    setProductAddFlowKey((prev) => prev + 1);
    setShowLubricationChart(true);
  }, []);

  const handleCloseLubricationChart = useCallback((): void => {
    setShowLubricationChart(false);
  }, []);

  // Wrapper refetch functions that work correctly in both edit and create modes
  const handleRefetchProductsWithSupplier = useCallback(async () => {
    if (isEditMode) {
      // In edit mode, refetch delivery products
      await refetchDeliveryProducts();
    } else {
      // In create mode, refetch products with supplier
      await refetchProductsWithSupplier();
    }
  }, [isEditMode, refetchDeliveryProducts, refetchProductsWithSupplier]);

  const handleRefetchProductsWithoutSupplier = useCallback(async () => {
    if (isEditMode) {
      // In edit mode, refetch delivery products
      await refetchDeliveryProducts();
    } else {
      // In create mode, refetch products without supplier
      await refetchProductsWithoutSupplier();
    }
  }, [isEditMode, refetchDeliveryProducts, refetchProductsWithoutSupplier]);

  return (
    <>
      {/* Delivery Information Card */}
      <DeliveryInformationCard
        deliveryInfo={deliveryInfo as any}
        deliveryType={selectedType}
        onBack={onBack}
        managerOptions={managerOptions}
        vesselOptions={vesselOptions}
        availableDates={availableDates}
        supplierOptions={supplierOptions}
        portOptions={portOptions}
        extraCostsOptions={extraCostsOptions}
      />

      {/* Products Table */}
      <ProductsTable
        onSave={handleSaveDelivery}
        submitting={isEditMode ? isUpdating : isCreating}
        products={localProducts}
        onProductChange={handleProductChange}
        isLoading={isLoadingProducts}
        onOpenLubricationChart={handleOpenLubricationChart}
        onCancel={handleCancel}
        deliveryType={selectedType}
      />

      <UnsavedChangesModal
        open={showCancelModal}
        onClose={handleCloseCancelModal}
        onLeaveWithoutSaving={handleConfirmCancel}
      />

      {/* Product Add Flow Modal */}
      <ProductAddFlowContainer
        key={productAddFlowKey}
        open={showLubricationChart}
        vesselId={effectiveVesselId}
        vesselName={
          vesselOptions?.find((v) => String(v.value) === String(deliveryFormData.vessel))?.label ||
          ''
        }
        supplierId={supplierId}
        onClose={handleCloseLubricationChart}
        deliveryType={selectedType}
        refetchProductsWithSupplier={handleRefetchProductsWithSupplier}
        refetchProductsWithoutSupplier={handleRefetchProductsWithoutSupplier}
        supplierIdForMakerList={supplierIdForMakerList}
      />
    </>
  );
};

export default ProductListingContainer;
